import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import activoService from '../../../services/activoService';
import { machineryService } from '../../../services/machineryService';
import { getAreas } from '../../../services/areaService';
import { getDocumentTypeLabel } from '../../../utils/documentTypes';
import { findFamilyPath, STATUS_OPTIONS } from './ActivoForm';
import { formatDateLocaleLima, formatDateToLima } from '../../../utils/dateUtils';
import Toast from '../../../components/Common/Toast';

const formatDocumentSeriesNumber = (document) => {
  if (!document) return '—';
  if (document.series && document.number) return `${document.series}-${document.number}`;
  return document.series || document.number || document.code || '—';
};

const formatDate = (value) => (value ? formatDateLocaleLima(value) || '—' : '—');

const toInputDate = (value) => formatDateToLima(value);

const iconBtnClass =
  'inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors';

const IconPlay = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const IconFileText = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />
  </svg>
);

const IconBarcode = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 5v14" /><path d="M8 5v14" /><path d="M12 5v14" /><path d="M17 5v14" /><path d="M21 5v14" />
  </svg>
);

const IconFileSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <circle cx="11" cy="13" r="3" />
    <path d="m16 18 2 2" />
  </svg>
);

const IconPencil = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.375 2.625a1 1 0 0 1 1.414 0l1.586 1.586a1 1 0 0 1 0 1.414L12 14.414 9.586 12l8.789-8.789Z" />
  </svg>
);

const IconCalendarEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M8 2v4" /><path d="M16 2v4" />
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <path d="M3 10h18" /><path d="M11 14h4" /><path d="M15 18h2" />
  </svg>
);

const IconArchive = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect width="20" height="5" x="2" y="3" rx="1" />
    <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
    <path d="M10 12h4" />
  </svg>
);

const IconLink = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const machineryTypeLabel = (type) => (type === 'vehicle' ? 'Vehículo' : type === 'machine' ? 'Máquina' : type || '—');

const ActivosListado = () => {
  const [activos, setActivos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [activoToStart, setActivoToStart] = useState(null);
  const [activityStartDate, setActivityStartDate] = useState('');
  const [usefulLifeYears, setUsefulLifeYears] = useState('');
  const [savingStart, setSavingStart] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [activoToEdit, setActivoToEdit] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editUsefulLifeYears, setEditUsefulLifeYears] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [retireModalOpen, setRetireModalOpen] = useState(false);
  const [activoToRetire, setActivoToRetire] = useState(null);
  const [retiredAt, setRetiredAt] = useState('');
  const [retirementNotes, setRetirementNotes] = useState('');
  const [retirePreview, setRetirePreview] = useState(null);
  const [loadingRetirePreview, setLoadingRetirePreview] = useState(false);
  const [savingRetire, setSavingRetire] = useState(false);
  const [machineryModalOpen, setMachineryModalOpen] = useState(false);
  const [activoToLink, setActivoToLink] = useState(null);
  const [machinerySearch, setMachinerySearch] = useState('');
  const [machineryResults, setMachineryResults] = useState([]);
  const [selectedMachinery, setSelectedMachinery] = useState(null);
  const [loadingMachinerySearch, setLoadingMachinerySearch] = useState(false);
  const [savingMachinery, setSavingMachinery] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [activosRes, areasRes] = await Promise.all([
        activoService.getActivos(),
        getAreas({ active_only: true }),
      ]);
      if (activosRes.success) setActivos(activosRes.data);
      if (areasRes.success && Array.isArray(areasRes.data)) setAreas(areasRes.data);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar activos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getStatusLabel = (status) => STATUS_OPTIONS.find((o) => o.value === status)?.label || status;

  const getStatusClasses = (status) => {
    if (status === 'pending_start') return 'bg-amber-50 text-amber-800';
    if (status === 'active') return 'bg-green-50 text-green-800';
    if (status === 'maintenance') return 'bg-orange-50 text-orange-800';
    if (status === 'inactive') return 'bg-gray-100 text-gray-700';
    if (status === 'retired') return 'bg-red-50 text-red-800';
    return 'bg-gray-100 text-gray-700';
  };

  const filteredActivos = useMemo(() => {
    return activos.filter((a) => {
      const docRef = formatDocumentSeriesNumber(a.document).toLowerCase();
      const matchesSearch =
        !searchTerm ||
        a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.area?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        docRef.includes(searchTerm.toLowerCase()) ||
        a.document?.code?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArea = !areaFilter || String(a.area_id) === String(areaFilter);
      const matchesStatus = !statusFilter || a.status === statusFilter;
      return matchesSearch && matchesArea && matchesStatus;
    });
  }, [activos, searchTerm, areaFilter, statusFilter]);

  const startPreview = useMemo(() => {
    if (!activoToStart || !usefulLifeYears) return null;
    const years = parseInt(usefulLifeYears, 10);
    if (!years || years < 1) return null;
    const qty = parseFloat(activoToStart.acquisition_quantity || 1);
    const price = parseFloat(activoToStart.acquisition_unit_price || 0);
    const base = qty * price;
    if (!base || base <= 0) return null;
    const monthly = base / (years * 12);
    const currency = activoToStart.acquisition_currency || 'PEN';
    const symbol = currency === 'USD' ? '$' : 'S/';
    return {
      base,
      monthly,
      annual: base / years,
      symbol,
    };
  }, [activoToStart, usefulLifeYears]);

  const openStartModal = (activo) => {
    setActivoToStart(activo);
    setActivityStartDate(new Date().toISOString().split('T')[0]);
    setUsefulLifeYears('');
    setStartModalOpen(true);
  };

  const closeStartModal = () => {
    setStartModalOpen(false);
    setActivoToStart(null);
    setActivityStartDate('');
    setUsefulLifeYears('');
  };

  const openEditModal = (activo) => {
    setActivoToEdit(activo);
    setEditDate(toInputDate(activo.activity_start_date));
    setEditUsefulLifeYears(activo.useful_life_years ? String(activo.useful_life_years) : '');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setActivoToEdit(null);
    setEditDate('');
    setEditUsefulLifeYears('');
  };

  const confirmStartActivities = async () => {
    if (!activoToStart || !activityStartDate || !usefulLifeYears) return;
    setSavingStart(true);
    try {
      const response = await activoService.startActivities(
        activoToStart.id,
        activityStartDate,
        parseInt(usefulLifeYears, 10),
      );
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Inicio de actividades registrado. El activo quedó en estado Activo.',
          type: 'success',
        });
        closeStartModal();
        loadData();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo registrar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al registrar inicio de actividades',
        type: 'error',
      });
    } finally {
      setSavingStart(false);
    }
  };

  const confirmEditActivity = async () => {
    if (!activoToEdit || !editDate || !editUsefulLifeYears) return;
    setSavingEdit(true);
    try {
      const response = await activoService.updateActivityStartDate(
        activoToEdit.id,
        editDate,
        parseInt(editUsefulLifeYears, 10),
      );
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Fecha y vida útil actualizadas correctamente',
          type: 'success',
        });
        closeEditModal();
        loadData();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo actualizar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al actualizar inicio de actividades',
        type: 'error',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const openRetireModal = (activo) => {
    setActivoToRetire(activo);
    setRetiredAt(new Date().toISOString().split('T')[0]);
    setRetirementNotes('');
    setRetirePreview(null);
    setRetireModalOpen(true);
  };

  const closeRetireModal = () => {
    setRetireModalOpen(false);
    setActivoToRetire(null);
    setRetiredAt('');
    setRetirementNotes('');
    setRetirePreview(null);
  };

  useEffect(() => {
    if (!retireModalOpen || !activoToRetire || !retiredAt) {
      setRetirePreview(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingRetirePreview(true);
    activoService.previewRetireActivo(activoToRetire.id, retiredAt)
      .then((res) => {
        if (!cancelled && res.success) setRetirePreview(res.data);
      })
      .catch(() => {
        if (!cancelled) setRetirePreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingRetirePreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [retireModalOpen, activoToRetire, retiredAt]);

  const confirmRetire = async () => {
    if (!activoToRetire || !retiredAt) return;
    setSavingRetire(true);
    try {
      const response = await activoService.retireActivo(activoToRetire.id, {
        retired_at: retiredAt,
        retirement_notes: retirementNotes.trim() || undefined,
      });
      if (response.success) {
        setToast({
          isOpen: true,
          message: response.message || 'Activo dado de baja correctamente',
          type: 'success',
        });
        closeRetireModal();
        loadData();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo dar de baja', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al dar de baja el activo',
        type: 'error',
      });
    } finally {
      setSavingRetire(false);
    }
  };

  const formatRetireMoney = (value) =>
    `S/ ${parseFloat(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const openMachineryModal = (activo) => {
    setActivoToLink(activo);
    setMachinerySearch('');
    setMachineryResults([]);
    setSelectedMachinery(activo.machinery_asset_summary || null);
    setMachineryModalOpen(true);
  };

  const closeMachineryModal = () => {
    setMachineryModalOpen(false);
    setActivoToLink(null);
    setMachinerySearch('');
    setMachineryResults([]);
    setSelectedMachinery(null);
  };

  useEffect(() => {
    if (!machineryModalOpen || machinerySearch.trim().length < 2) {
      setMachineryResults([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingMachinerySearch(true);
      try {
        const term = machinerySearch.trim();
        const [machinesRes, vehiclesRes] = await Promise.all([
          machineryService.getMachines({ search: term }),
          machineryService.getVehicles({ search: term }),
        ]);

        if (cancelled) return;

        const results = [];
        if (machinesRes.success && Array.isArray(machinesRes.data)) {
          machinesRes.data.forEach((item) => {
            results.push({
              type: 'machine',
              id: item.id,
              code: item.code,
              name: item.name,
              brand: item.brand,
              model: item.model,
              status: item.status,
            });
          });
        }
        if (vehiclesRes.success && Array.isArray(vehiclesRes.data)) {
          vehiclesRes.data.forEach((item) => {
            results.push({
              type: 'vehicle',
              id: item.id,
              code: item.code,
              name: item.name,
              brand: item.brand,
              model: item.model,
              status: item.status,
            });
          });
        }

        results.sort((a, b) => a.code.localeCompare(b.code, 'es'));
        setMachineryResults(results.slice(0, 20));
      } catch {
        if (!cancelled) setMachineryResults([]);
      } finally {
        if (!cancelled) setLoadingMachinerySearch(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [machineryModalOpen, machinerySearch]);

  const lookupMachineryByCode = async () => {
    const code = machinerySearch.trim();
    if (!code) return;

    setLoadingMachinerySearch(true);
    try {
      const response = await activoService.lookupMachinery(code);
      if (response.success) {
        setSelectedMachinery(response.data);
        setMachineryResults([]);
      } else {
        setSelectedMachinery(null);
        setToast({
          isOpen: true,
          message: response.message || 'No se encontró maquinaria con ese código',
          type: 'error',
        });
      }
    } catch (error) {
      setSelectedMachinery(null);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al buscar maquinaria',
        type: 'error',
      });
    } finally {
      setLoadingMachinerySearch(false);
    }
  };

  const confirmLinkMachinery = async () => {
    if (!activoToLink || !selectedMachinery) return;

    setSavingMachinery(true);
    try {
      const response = await activoService.linkMachinery(activoToLink.id, {
        machinery_asset_type: selectedMachinery.type,
        machinery_asset_id: selectedMachinery.id,
      });
      if (response.success) {
        setToast({
          isOpen: true,
          message: response.message || 'Activo vinculado a maquinaria',
          type: 'success',
        });
        closeMachineryModal();
        loadData();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo vincular', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al vincular maquinaria',
        type: 'error',
      });
    } finally {
      setSavingMachinery(false);
    }
  };

  const confirmUnlinkMachinery = async () => {
    if (!activoToLink) return;

    setSavingMachinery(true);
    try {
      const response = await activoService.linkMachinery(activoToLink.id, { disconnect: true });
      if (response.success) {
        setToast({
          isOpen: true,
          message: response.message || 'Conexión eliminada',
          type: 'success',
        });
        closeMachineryModal();
        loadData();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo desvincular', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al desvincular maquinaria',
        type: 'error',
      });
    } finally {
      setSavingMachinery(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Listado de activos</h1>
            <p className="text-gray-600 mt-1">
              Consulte activos registrados desde compras o cargados como inventario inicial.
              Asigne inicio de actividades con vida útil y corrija fechas desde este listado.
            </p>
          </div>
          <Link
            to="/activos/listado/cargar-iniciales"
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium text-center whitespace-nowrap"
          >
            Activos iniciales
          </Link>
        </div>

        <nav className="text-sm text-gray-600 mb-4">
          <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
          <span className="mx-2">/</span>
          <Link to="/activos" className="hover:text-teal-600">Activos</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Listado</span>
        </nav>

        <div className="flex flex-wrap gap-3 mb-6">
          <input
            type="text"
            placeholder="Buscar por código, nombre, marca, área o documento…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-md"
          />
          <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
            <option value="">Todas las áreas</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
            <option value="">Todos los estados</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando activos…</div>
          ) : filteredActivos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay activos registrados</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clasificación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Área</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Maquinaria</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documento origen</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inicio actividades</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vida útil</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredActivos.map((activo) => {
                  const canStartActivities =
                    activo.status === 'pending_start' && !activo.activity_start_date;
                  const canEditActivity = Boolean(activo.activity_start_date) && activo.status !== 'retired';
                  const canRetire = Boolean(activo.activity_start_date) && activo.status !== 'retired';

                  return (
                    <tr key={activo.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{activo.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{activo.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{findFamilyPath(activo.family)}</td>
                      <td className="px-6 py-4 text-sm">
                        {activo.area ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-800 text-xs font-medium">
                            {activo.area.name}
                          </span>
                        ) : (
                          <span className="text-gray-400">Sin área</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {activo.machinery_asset_summary ? (
                          <div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-50 text-indigo-800 text-xs font-medium">
                              {activo.machinery_asset_summary.code}
                            </span>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {machineryTypeLabel(activo.machinery_asset_summary.type)}
                              {' · '}
                              {activo.machinery_asset_summary.name}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400">Sin vincular</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {activo.document ? (
                          <div>
                            <Link
                              to={`/documents/${activo.document.id}/edit`}
                              className="font-medium text-teal-700 hover:text-teal-900"
                              title={activo.document.code ? `Código interno: ${activo.document.code}` : undefined}
                            >
                              {formatDocumentSeriesNumber(activo.document)}
                            </Link>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {getDocumentTypeLabel(activo.document.document_type)}
                              {activo.document.code ? ` · ${activo.document.code}` : ''}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400">Sin documento</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {activo.activity_start_date ? formatDate(activo.activity_start_date) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {activo.useful_life_years ? `${activo.useful_life_years} año${activo.useful_life_years !== 1 ? 's' : ''}` : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusClasses(activo.status)}`}>
                          {getStatusLabel(activo.status)}
                        </span>
                        {activo.retired_at && (
                          <p className="text-xs text-gray-500 mt-1">Baja: {formatDate(activo.retired_at)}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openMachineryModal(activo)}
                            className={`${iconBtnClass} text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50`}
                            title={activo.machinery_asset_summary ? 'Gestionar vínculo con maquinaria' : 'Vincular con maquinaria'}
                            aria-label="Vincular con maquinaria"
                          >
                            <IconLink />
                          </button>
                          {canStartActivities && (
                            <button
                              type="button"
                              onClick={() => openStartModal(activo)}
                              className={`${iconBtnClass} text-teal-600 hover:text-teal-800 hover:bg-teal-50`}
                              title="Iniciar actividades"
                              aria-label="Iniciar actividades"
                            >
                              <IconPlay />
                            </button>
                          )}
                          {canEditActivity && (
                            <button
                              type="button"
                              onClick={() => openEditModal(activo)}
                              className={`${iconBtnClass} text-amber-600 hover:text-amber-800 hover:bg-amber-50`}
                              title="Corregir fecha y vida útil"
                              aria-label="Corregir fecha y vida útil"
                            >
                              <IconCalendarEdit />
                            </button>
                          )}
                          {canRetire && (
                            <button
                              type="button"
                              onClick={() => openRetireModal(activo)}
                              className={`${iconBtnClass} text-red-600 hover:text-red-800 hover:bg-red-50`}
                              title="Dar de baja"
                              aria-label="Dar de baja"
                            >
                              <IconArchive />
                            </button>
                          )}
                          <Link
                            to={`/activos/listado/${activo.id}/print`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={iconBtnClass}
                            title="Ficha técnica"
                            aria-label="Ficha técnica"
                          >
                            <IconFileText />
                          </Link>
                          <Link
                            to={`/activos/listado/${activo.id}/print-barcode`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={iconBtnClass}
                            title="Imprimir código de barras"
                            aria-label="Imprimir código de barras"
                          >
                            <IconBarcode />
                          </Link>
                          {activo.document_id ? (
                            <Link
                              to={`/documents/${activo.document_id}/edit`}
                              className={`${iconBtnClass} text-purple-600 hover:text-purple-800 hover:bg-purple-50`}
                              title="Ver documento"
                              aria-label="Ver documento"
                            >
                              <IconFileSearch />
                            </Link>
                          ) : (
                            <Link
                              to={`/activos/listado/${activo.id}/edit`}
                              className={`${iconBtnClass} text-purple-600 hover:text-purple-800 hover:bg-purple-50`}
                              title="Editar ficha"
                              aria-label="Editar ficha"
                            >
                              <IconPencil />
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {startModalOpen && activoToStart && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Inicio de actividades</h3>
            <p className="text-sm text-gray-600 mb-4">
              {activoToStart.code} — {activoToStart.name}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              Al confirmar, el activo pasará de <strong>Pendiente inicio de actividades</strong> a <strong>Activo</strong>.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio de actividades *</label>
            <input
              type="date"
              value={activityStartDate}
              onChange={(e) => setActivityStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Vida útil (años) *</label>
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={usefulLifeYears}
              onChange={(e) => setUsefulLifeYears(e.target.value)}
              placeholder="Ej. 10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
            />
            {startPreview && (
              <div className="text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 mb-4">
                Costo sin IGV:{' '}
                <strong>
                  {startPreview.symbol}{' '}
                  {startPreview.base.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
                {' · '}
                Cuota anual:{' '}
                <strong>
                  {startPreview.symbol}{' '}
                  {startPreview.annual.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
                {' · '}
                Cuota mensual:{' '}
                <strong>
                  {startPreview.symbol}{' '}
                  {startPreview.monthly.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeStartModal}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={savingStart}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmStartActivities}
                disabled={savingStart || !activityStartDate || !usefulLifeYears}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {savingStart ? 'Guardando…' : 'Confirmar inicio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && activoToEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Corregir inicio de actividades</h3>
            <p className="text-sm text-gray-600 mb-4">
              {activoToEdit.code} — {activoToEdit.name}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              Fecha actual: <strong>{formatDate(activoToEdit.activity_start_date)}</strong>
              {activoToEdit.useful_life_years ? (
                <> · Vida útil: <strong>{activoToEdit.useful_life_years} años</strong></>
              ) : null}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio de actividades *</label>
            <input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Vida útil (años) *</label>
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={editUsefulLifeYears}
              onChange={(e) => setEditUsefulLifeYears(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-6"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEditModal}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={savingEdit}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmEditActivity}
                disabled={savingEdit || !editDate || !editUsefulLifeYears}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {savingEdit ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {retireModalOpen && activoToRetire && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Dar de baja activo</h3>
            <p className="text-sm text-gray-600 mb-4">
              {activoToRetire.code} — {activoToRetire.name}
            </p>
            <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
              La depreciación se corta en el mes de baja. Si queda saldo pendiente, se cargará
              <strong> completo en ese mes</strong> en los reportes.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de baja *</label>
            <input
              type="date"
              value={retiredAt}
              min={toInputDate(activoToRetire.activity_start_date) || undefined}
              onChange={(e) => setRetiredAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo / notas</label>
            <textarea
              value={retirementNotes}
              onChange={(e) => setRetirementNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              placeholder="Opcional: venta, siniestro, obsolescencia…"
            />
            {loadingRetirePreview && (
              <p className="text-xs text-gray-500 mb-4">Calculando impacto…</p>
            )}
            {retirePreview && !loadingRetirePreview && (
              <div className="text-sm bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 mb-4 space-y-1">
                <p>
                  Depreciado antes del mes de baja:{' '}
                  <strong>{formatRetireMoney(retirePreview.accumulated_before)}</strong>
                </p>
                <p>
                  Cargo en mes de baja ({retirePreview.retirement_month}):{' '}
                  <strong>{formatRetireMoney(retirePreview.remaining_to_charge)}</strong>
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeRetireModal}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={savingRetire}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmRetire}
                disabled={savingRetire || !retiredAt}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {savingRetire ? 'Procesando…' : 'Confirmar baja'}
              </button>
            </div>
          </div>
        </div>
      )}

      {machineryModalOpen && activoToLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Vincular con maquinaria</h3>
            <p className="text-sm text-gray-600 mb-4">
              {activoToLink.code} — {activoToLink.name}
            </p>

            {activoToLink.machinery_asset_summary && (
              <div className="text-sm bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-4">
                <p className="font-medium text-indigo-900">Vinculado actualmente</p>
                <p className="text-indigo-800 mt-1">
                  {machineryTypeLabel(activoToLink.machinery_asset_summary.type)}{' '}
                  <strong>{activoToLink.machinery_asset_summary.code}</strong>
                  {' — '}
                  {activoToLink.machinery_asset_summary.name}
                </p>
                <button
                  type="button"
                  onClick={confirmUnlinkMachinery}
                  disabled={savingMachinery}
                  className="mt-2 text-xs text-red-700 hover:text-red-900 underline disabled:opacity-50"
                >
                  Desvincular
                </button>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">Código de maquinaria</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={machinerySearch}
                onChange={(e) => {
                  setMachinerySearch(e.target.value);
                  setSelectedMachinery(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    lookupMachineryByCode();
                  }
                }}
                placeholder="Buscar por código, nombre o marca…"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button
                type="button"
                onClick={lookupMachineryByCode}
                disabled={loadingMachinerySearch || !machinerySearch.trim()}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
              >
                Buscar código
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Escriba al menos 2 caracteres para ver sugerencias o use «Buscar código» para coincidencia exacta.
            </p>

            {loadingMachinerySearch && (
              <p className="text-xs text-gray-500 mb-3">Buscando maquinaria…</p>
            )}

            {machineryResults.length > 0 && (
              <div className="border border-gray-200 rounded-lg mb-4 max-h-48 overflow-y-auto divide-y divide-gray-100">
                {machineryResults.map((item) => {
                  const isSelected = selectedMachinery?.type === item.type && selectedMachinery?.id === item.id;
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      onClick={() => setSelectedMachinery(item)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                    >
                      <span className="font-medium text-gray-900">{item.code}</span>
                      <span className="text-gray-500"> · {machineryTypeLabel(item.type)}</span>
                      <p className="text-xs text-gray-600 truncate">{item.name}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedMachinery && (
              <div className="text-sm bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 mb-4">
                <p className="font-medium text-teal-900">Seleccionado para vincular</p>
                <p className="text-teal-800 mt-1">
                  {machineryTypeLabel(selectedMachinery.type)}{' '}
                  <strong>{selectedMachinery.code}</strong>
                  {' — '}
                  {selectedMachinery.name}
                </p>
                {(selectedMachinery.brand || selectedMachinery.model) && (
                  <p className="text-xs text-teal-700 mt-1">
                    {[selectedMachinery.brand, selectedMachinery.model].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeMachineryModal}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={savingMachinery}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmLinkMachinery}
                disabled={
                  savingMachinery
                  || !selectedMachinery
                  || (
                    activoToLink.machinery_asset_summary
                    && selectedMachinery.type === activoToLink.machinery_asset_summary.type
                    && selectedMachinery.id === activoToLink.machinery_asset_summary.id
                  )
                }
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingMachinery ? 'Guardando…' : 'Vincular'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ActivosListado;
